import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api.js';
import ConfirmModal from './ConfirmModal';
import NoticeModal from './NoticeModal';

const WithdrawalPoolPage = ({ panelId }) => {
    const [withdrawals, setWithdrawals] = useState([]);
    const _cu = JSON.parse(localStorage.getItem('user') || '{}');
    const _role = _cu.role;
    const canDoWithdraw = _role === 'izleyici' ? false : (_role === 'yardimci' ? !!_cu.canWithdraw : true);
    const isYonetimRole = _role === 'yonetim' || _role === 'master_admin';
    const isAdminRole = isYonetimRole || _role === 'admin';
    const canMoveWithdrawal = isAdminRole || _role === 'finansci' || _role === 'destek';
    const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
    const [confirmState, setConfirmState] = useState({ open: false, message: '', onConfirm: null, danger: false });
    const askConfirm = (message, onConfirm, danger = false) => setConfirmState({ open: true, message, onConfirm, danger });
    const closeConfirm = () => setConfirmState({ open: false, message: '', onConfirm: null, danger: false });
    const [notice, setNotice] = useState({ open: false, message: '', danger: false });
    const showNotice = (message, danger = false) => setNotice({ open: true, message, danger });
    const [viewedReceipt, setViewedReceipt] = useState(null); // görüntülenen dekont
    const [showFilters, setShowFilters] = useState(false);
    const isManagement = window.location.hostname.startsWith('yonetim');
    const [banks, setBanks] = useState([]);
    const [panels, setPanels] = useState([]);
    const [forwardTarget, setForwardTarget] = useState('');
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        minAmount: '',
        maxAmount: '',
        searchTerm: ''
    });

    // Yeni İstek Modal State'leri
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newRequest, setNewRequest] = useState({
        name: '',
        username: '',
        bank: '',
        iban: '',
        amount: ''
    });

    const fetchWithdrawals = useCallback(async () => {
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            const activePanelId = panelId || currentUser?.panelId;
            // new hariç diğer statüleri getir
            const res = await api.getWithdrawals({ status: 'processing,approved,rejected,awaiting_mgmt', panelId: activePanelId || '' });
            setWithdrawals(res.data);
        } catch (err) {
            console.error(err);
        }
    }, [panelId]);

    const fetchBanks = async () => {
        try {
            const res = await api.getBanks();
            setBanks(res.data);
        } catch (error) {
            console.error("Bankalar yüklenemedi", error);
        }
    };

    const fetchPanels = async () => {
        try {
            const res = await api.getPanels();
            setPanels(res.data);
        } catch (error) {
            console.error("Paneller yüklenemedi", error);
        }
    };

    useEffect(() => {
        document.title = 'PayzoPay - Çekim Havuzu';
        fetchWithdrawals();
        fetchBanks();
        if (isManagement) fetchPanels();
        const interval = setInterval(fetchWithdrawals, 5000);
        return () => clearInterval(interval);
    }, [fetchWithdrawals, isManagement]);

    const handleForward = async (id, targetPanelId) => {
        if (!targetPanelId) { showNotice('Lütfen bir grup seçin.', true); return; }
        try {
            await api.forwardWithdrawal(id, targetPanelId);
            setSelectedWithdrawal(null);
            setForwardTarget('');
            fetchWithdrawals();
            logAction('Çekim İletme', `İstek ID: ${id} -> grup ${targetPanelId} iletildi.`);
        } catch (error) {
            showNotice('İletme başarısız: ' + (error.response?.data?.error || error.message), true);
        }
    };

    const logAction = async (action, details) => {
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            const activePanelId = panelId || user?.panelId;
            const username = user ? user.username : 'Bilinmiyor';
            await api.createLog({ username, action, details, panelId: activePanelId });
        } catch (error) {
            console.error('Loglama hatası:', error);
        }
    };

    const updateStatus = async (id, status) => {
        try {
            const res = await api.updateWithdrawalStatus(id, status);
            fetchWithdrawals();
            logAction('Çekim Durum Güncelleme', `İstek ID: ${id}, Yeni Durum: ${status}`);
            // 51.000+ onayı Yüksek Çekimler listesine düştüyse operatörü bilgilendir.
            if (status === 'approved' && res.data?.data?.status === 'awaiting_mgmt') {
                showNotice('Bu çekim Yüksek Çekimler onayına gönderildi. Firma tarafından dekont kontrolü yapılmaktadır, lütfen bekleyin.');
            }
        } catch (error) {
            showNotice('Güncelleme hatası: ' + (error.response?.data?.error || error.message), true);
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.deleteWithdrawal(id);
            fetchWithdrawals();
            logAction('Çekim Silme', `İstek ID: ${id} silindi.`);
        } catch (error) {
            showNotice('Silme başarısız.', true);
        }
    };

    const handleReceiptUpload = (id, file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                await api.uploadReceipt(id, { receipt: reader.result, name: file.name, type: file.type });
                setSelectedWithdrawal(prev => (prev && prev._id === id) ? { ...prev, receiptName: file.name } : prev);
                showNotice('Dekont yüklendi.');
            } catch (error) {
                showNotice('Dekont yüklenemedi: ' + (error.response?.data?.error || error.message), true);
            }
        };
        reader.readAsDataURL(file);
    };

    const viewReceipt = async (id) => {
        try {
            const res = await api.getReceipt(id);
            setViewedReceipt(res.data);
        } catch (error) {
            showNotice('Dekont bulunamadı.', true);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const filteredWithdrawals = withdrawals.filter(req => {
        // Arama Filtresi
        const term = filters.searchTerm.toLowerCase();
        const matchesSearch = !term ||
            (req.name && req.name.toLowerCase().includes(term)) ||
            (req.username && req.username.toLowerCase().includes(term)) ||
            (req.bank && req.bank.toLowerCase().includes(term)) ||
            (req.amount && req.amount.toString().includes(term));

        // Tarih Filtresi
        const reqDate = new Date(req.createdAt);
        const start = filters.startDate ? new Date(filters.startDate) : null;
        const end = filters.endDate ? new Date(filters.endDate) : null;
        if (start) start.setHours(0, 0, 0, 0);
        if (end) end.setHours(23, 59, 59, 999);
        const matchesDate = (!start || reqDate >= start) && (!end || reqDate <= end);

        // Miktar Filtresi
        const amount = req.amount;
        const min = filters.minAmount ? parseFloat(filters.minAmount) : null;
        const max = filters.maxAmount ? parseFloat(filters.maxAmount) : null;
        const matchesAmount = (!min || amount >= min) && (!max || amount <= max);

        return matchesSearch && matchesDate && matchesAmount;
    });



    const getStatusColor = (status) => {
        switch (status) {
            case 'new': return '#FFCE54'; // Pastel Sarı
            case 'processing': return '#F6BB42'; // Pastel Turuncu
            case 'approved': return '#A0D468'; // Pastel Yeşil
            case 'rejected': return '#ED5565'; // Pastel Kırmızı
            default: return '#333';
        }
    };

    const getStatusRowBackground = (status) => {
        switch (status) {
            case 'new': return 'rgba(255, 206, 84, 0.5)';
            case 'processing': return 'rgba(246, 187, 66, 0.5)';
            case 'approved': return 'rgba(160, 212, 104, 0.5)';
            case 'rejected': return 'rgba(237, 85, 101, 0.5)';
            default: return 'transparent';
        }
    };

    const labelStyle = { fontWeight: 'bold', fontSize: '0.9em', marginBottom: '5px', display: 'block', color: 'var(--text-color)' };
    const inputStyle = { width: '100%', padding: '8px', border: '1px solid var(--input-border)', borderRadius: '4px', boxSizing: 'border-box', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' };

    const cardStyle = {
        backgroundColor: 'var(--card-bg)',
        borderRadius: '15px',
        boxShadow: 'var(--shadow)',
        overflow: 'hidden',
        marginBottom: '20px',
        color: 'var(--text-color)'
    };

    const handleCreateChange = (e) => {
        const { name, value } = e.target;
        setNewRequest(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        const isPanelDeleted = isManagement && !!panelId && panels.length > 0 && !panels.some(p => p.panelId === panelId);
        if (isPanelDeleted) {
            showNotice('Bu departman silinmiş veya bulunamadı. Yeni çekim eklenemez.', true);
            return;
        }
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            const activePanelId = panelId || currentUser?.panelId;
            await api.createAdminWithdrawal({
                ...newRequest,
                panelId: activePanelId
            });
            setShowCreateModal(false);
            setNewRequest({ name: '', username: '', bank: '', iban: '', amount: '' });
            fetchWithdrawals();
        } catch (error) {
            showNotice('Oluşturulurken hata oluştu: ' + (error.response?.data?.error || error.message), true);
        }
    };

    const isPanelDeleted = isManagement && !!panelId && panels.length > 0 && !panels.some(p => p.panelId === panelId);

    return (
        <div style={{ padding: '20px' }}>
            {isPanelDeleted && (
                <div style={{ padding: '12px 16px', background: 'rgba(220,53,69,0.15)', border: '1px solid #dc3545', borderRadius: '8px', color: '#dc3545', marginBottom: '15px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fas fa-exclamation-triangle"></i>
                    Bu departman silinmiş veya bulunamadı. Yeni çekim eklenemez.
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h2 style={{ margin: 0, color: '#fff', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ background: '#dc3545', color: '#fff', padding: '4px 14px', borderRadius: '6px', fontSize: '0.6em', fontWeight: 'bold', letterSpacing: '1px' }}>ÇEKİM</span>
                    Çekim Havuzu
                </h2>
                <div>
                    {isManagement && canDoWithdraw && !isPanelDeleted && (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '20px',
                                border: 'none',
                                backgroundColor: '#28a745',
                                color: '#fff',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                marginRight: '10px'
                            }}
                        >
                            + Yeni Ekle
                        </button>
                    )}
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        style={{
                            padding: '8px 16px',
                            borderRadius: '20px',
                            border: 'none',
                            backgroundColor: '#007bff',
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                        }}
                    >
                        {showFilters ? 'Filtreleri Gizle' : 'Filtreleri Göster'}
                    </button>
                </div>
            </div>

            {/* Filtre Alanı */}
            {showFilters && (
                <div style={{ ...cardStyle, padding: '20px', backgroundColor: 'var(--table-header-bg)' }}>
                    <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>

                            {/* Tarih Filtreleri */}
                            <div>
                                <label style={labelStyle}>Başlangıç Tarihi</label>
                                <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} style={inputStyle} />
                            </div>
                            <div>
                                <label style={labelStyle}>Bitiş Tarihi</label>
                                <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} style={inputStyle} />
                            </div>

                            {/* Miktar Filtreleri */}
                            <div>
                                <label style={labelStyle}>Minimum Miktar</label>
                                <input type="number" name="minAmount" placeholder="0" value={filters.minAmount} onChange={handleFilterChange} style={inputStyle} />
                            </div>
                            <div>
                                <label style={labelStyle}>Maksimum Miktar</label>
                                <input type="number" name="maxAmount" placeholder="0" value={filters.maxAmount} onChange={handleFilterChange} style={inputStyle} />
                            </div>

                            {/* Dropdownlar */}
                            <div>
                                <label style={labelStyle}>İşlem Durumu</label>
                                <select style={inputStyle}>
                                    <option value="">Hepsi</option>
                                    <option value="Havale">Havale</option>
                                </select>
                            </div>
                            <div>
                                <label style={labelStyle}>Site</label>
                                <select style={inputStyle}>
                                    <option value="">Hepsi</option>
                                    <option value="Yatırım">Yatırım</option>
                                </select>
                            </div>
                            <div>
                                <label style={labelStyle}>Finans Grubu</label>
                                <select style={inputStyle}>
                                    <option value="">Hepsi</option>
                                    <option value="Banka">Banka</option>
                                </select>
                            </div>
                        </div>

                        {/* Arama */}
                        <div style={{ marginTop: '15px' }}>
                            <label style={labelStyle}>Arama</label>
                            <input type="text" name="searchTerm" placeholder="İsim, Kullanıcı, Herşey..." value={filters.searchTerm} onChange={handleFilterChange} style={inputStyle} />
                        </div>
                    </div>
                </div>
            )}



            {/* Tüm Talepler Tablosu + Detay Paneli */}
            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
            <div style={{ flex: selectedWithdrawal ? '0 0 68%' : '1', transition: 'all 0.3s', minWidth: 0 }}>
            <div style={cardStyle}>
                <h3 style={{ margin: '20px 0 10px 20px', color: 'var(--text-color)' }}>Tüm Talepler</h3>
                <div style={{ padding: '0 20px 20px 20px' }}>
                    <table cellPadding="15" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 10px', color: 'var(--text-color)' }}>
                        <thead>
                            <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '2px solid var(--border-color)', textAlign: 'left' }}>
                                <th>ID</th>
                                <th>İsim Soyisim</th>
                                <th>Kullanıcı Adı</th>
                                <th>Banka Adı</th>
                                <th>Miktar</th>
                                <th>İşlemi Yapan</th>
                                <th>Tarih</th>
                                <th style={{ width: '120px', textAlign: 'center' }}>Durum</th>
                                <th>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredWithdrawals.length === 0 && (
                                <tr>
                                    <td colSpan="9" style={{ textAlign: 'center' }}>Veri bulunamadı</td>
                                </tr>
                            )}
                            {filteredWithdrawals.map(req => {
                                const rowBg = getStatusRowBackground(req.status);
                                const radius = '15px';
                                return (
                                    <tr key={req._id} onClick={() => setSelectedWithdrawal(req)} style={{ cursor: 'pointer' }}>
                                        <td style={{ backgroundColor: rowBg, borderTopLeftRadius: radius, borderBottomLeftRadius: radius }}>{req.siteTransactionNo || ('#' + String(req._id).slice(-6))}</td>
                                        <td style={{ backgroundColor: rowBg }}>{req.name}</td>
                                        <td style={{ backgroundColor: rowBg }}>{req.username}</td>
                                        <td style={{ backgroundColor: rowBg }}>{req.bank}</td>
                                        <td style={{ backgroundColor: rowBg }}>{Number(req.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                        <td style={{ backgroundColor: rowBg }}>{req.processedBy || '-'}</td>
                                        <td style={{ backgroundColor: rowBg }}>{new Date(req.createdAt).toLocaleString()}</td>
                                        <td style={{ backgroundColor: rowBg, padding: 0, height: '1px' }}>
                                            <div style={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: getStatusColor(req.status),
                                                color: '#fff',
                                                fontWeight: 'bold',
                                                minHeight: '40px'
                                            }}>
                                                {req.status === 'processing' ? 'İŞLEMDE' : req.status === 'awaiting_mgmt' ? 'DEKONT KONTROLÜNDE' : req.status === 'approved' ? 'ONAYLANDI' : 'REDDEDİLDİ'}
                                            </div>
                                        </td>
                                        <td style={{ backgroundColor: rowBg, borderTopRightRadius: radius, borderBottomRightRadius: radius, verticalAlign: 'middle', padding: '5px' }}>
                                            <div style={{ display: 'flex', width: '95%', margin: '0 auto', gap: '5px' }}>
                                                {canDoWithdraw && req.status === 'processing' && (
                                                    <>
                                                        <button onClick={(e) => { e.stopPropagation(); askConfirm('Bu çekim talebini ONAYLAMAK istediğinize emin misiniz?', () => updateStatus(req._id, 'approved')); }} style={{ flex: 1, backgroundColor: '#28a745', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 'bold', borderRadius: '4px', padding: '8px 0' }}>ONAYLA</button>
                                                        <button onClick={(e) => { e.stopPropagation(); askConfirm('Bu çekim talebini REDDETMEK istediğinize emin misiniz?', () => updateStatus(req._id, 'rejected'), true); }} style={{ flex: 1, backgroundColor: '#dc3545', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 'bold', borderRadius: '4px', padding: '8px 0' }}>REDDET</button>
                                                    </>
                                                )}
                                                {isManagement && isYonetimRole && (
                                                    <button onClick={(e) => { e.stopPropagation(); askConfirm('Bu çekim talebini SİLMEK istediğinize emin misiniz? Bu işlem geri alınamaz.', () => handleDelete(req._id), true); }} style={{ flex: (canDoWithdraw && req.status === 'processing') ? 0.6 : 1, backgroundColor: '#6c757d', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 'bold', borderRadius: '4px', padding: '8px 0' }}>SİL</button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            </div>

                {selectedWithdrawal && (
                    <div style={{ flex: '1', ...cardStyle, padding: '20px', position: 'sticky', top: '20px', minWidth: '280px' }}>
                        <h3 style={{ marginTop: 0, borderBottom: '2px solid var(--border-color)', paddingBottom: '15px', color: 'var(--text-color)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ background: '#dc3545', color: '#fff', padding: '4px 12px', borderRadius: '6px', fontSize: '0.7em', fontWeight: 'bold', letterSpacing: '1px' }}>ÇEKİM</span>
                            Çekim Detayları
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px', fontSize: '0.9em', color: 'var(--text-color)' }}>
                            <div><strong>İsim:</strong><br />{selectedWithdrawal.name || '-'}</div>
                            <div><strong>Kullanıcı Adı:</strong><br />{selectedWithdrawal.username || '-'}</div>
                            <div style={{ gridColumn: 'span 2' }}><strong>Banka / IBAN:</strong><br />{selectedWithdrawal.bank || '-'}</div>
                            <div><strong>Tutar:</strong><br />{Number(selectedWithdrawal.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            <div><strong>Durum:</strong><br />{selectedWithdrawal.status}</div>
                            <div><strong>Tarih:</strong><br />{new Date(selectedWithdrawal.createdAt).toLocaleString()}</div>
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>Dekont:</label>
                            {canDoWithdraw && (
                                <input type="file" accept="image/*,application/pdf" onChange={(e) => handleReceiptUpload(selectedWithdrawal._id, e.target.files[0])} />
                            )}
                            {selectedWithdrawal.receiptName ? (
                                <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '0.85em', color: '#28a745' }}>Yüklü: {selectedWithdrawal.receiptName}</span>
                                    <button onClick={() => viewReceipt(selectedWithdrawal._id)} style={{ padding: '4px 10px', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85em' }}>Dekontu Görüntüle</button>
                                </div>
                            ) : (
                                <div style={{ fontSize: '0.8em', opacity: 0.6, marginTop: '4px' }}>Henüz dekont yüklenmemiş.</div>
                            )}
                        </div>

                        {isManagement && canMoveWithdrawal && (
                            <div style={{ marginBottom: '15px', paddingTop: '12px', borderTop: '1px solid var(--border-color)' }}>
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>Başka Departmana Taşı:</label>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <select value={forwardTarget} onChange={(e) => setForwardTarget(e.target.value)} style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text-color)' }}>
                                        <option value="">Departman seçin...</option>
                                        {panels.filter(p => p.panelId !== selectedWithdrawal.panelId).map(p => (
                                            <option key={p._id} value={p.panelId}>{p.name || p.panelId}</option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={() => {
                                            const target = forwardTarget;
                                            const tName = (panels.find(p => p.panelId === target) || {}).name || target;
                                            if (!target) { showNotice('Lütfen bir departman seçin.', true); return; }
                                            askConfirm(`Bu çekimi "${tName}" departmanına taşımak istediğinize emin misiniz?`, () => handleForward(selectedWithdrawal._id, target));
                                        }}
                                        style={{ padding: '8px 12px', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}
                                    >
                                        Taşı
                                    </button>
                                </div>
                            </div>
                        )}

                        <button onClick={() => setSelectedWithdrawal(null)} style={{ width: '100%', padding: '8px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Kapat</button>
                    </div>
                )}
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
                }}>
                    <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '8px', width: '90%', maxWidth: '400px', color: 'var(--text-color)' }}>
                        <h3>Yeni Çekim Talebi</h3>
                        <form onSubmit={handleCreateSubmit}>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={labelStyle}>İsim Soyisim</label>
                                <input name="name" value={newRequest.name} onChange={handleCreateChange} required style={inputStyle} />
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={labelStyle}>Kullanıcı Adı</label>
                                <input name="username" value={newRequest.username} onChange={handleCreateChange} required style={inputStyle} />
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={labelStyle}>Banka</label>
                                <select name="bank" value={newRequest.bank} onChange={handleCreateChange} required style={inputStyle}>
                                    <option value="">Banka Seçiniz</option>
                                    {banks.map(bank => (
                                        <option key={bank._id} value={bank.displayName}>{bank.displayName}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={labelStyle}>IBAN</label>
                                <input name="iban" value={newRequest.iban} onChange={handleCreateChange} required style={inputStyle} placeholder="TR.." />
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={labelStyle}>Miktar</label>
                                <input type="number" name="amount" value={newRequest.amount} onChange={handleCreateChange} required style={inputStyle} />
                            </div>
                            <div style={{ textAlign: 'right', marginTop: '20px' }}>
                                <button type="button" onClick={() => setShowCreateModal(false)} style={{ marginRight: '10px', padding: '8px 16px', background: '#ccc', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>İptal</button>
                                <button type="submit" style={{ padding: '8px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Kaydet</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Dekont görüntüleyici */}
            {viewedReceipt && (
                <div onClick={() => setViewedReceipt(null)} style={{ position: 'fixed', inset: 0, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 3500 }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: '16px', borderRadius: '8px', maxWidth: '90%', maxHeight: '90%', overflow: 'auto', textAlign: 'center' }}>
                        <div style={{ marginBottom: '10px', color: '#000', fontWeight: 'bold' }}>{viewedReceipt.name || 'Dekont'}</div>
                        {((viewedReceipt.type || '').includes('pdf') || (viewedReceipt.receipt || '').startsWith('data:application/pdf')) ? (
                            <iframe title="dekont" src={viewedReceipt.receipt} style={{ width: '80vw', height: '75vh', border: 'none' }} />
                        ) : (
                            <img src={viewedReceipt.receipt} alt="Dekont" style={{ maxWidth: '80vw', maxHeight: '75vh' }} />
                        )}
                        <div style={{ marginTop: '10px' }}>
                            <a href={viewedReceipt.receipt} download={viewedReceipt.name || 'dekont'} style={{ marginRight: '12px' }}>İndir</a>
                            <button onClick={() => setViewedReceipt(null)} style={{ padding: '6px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Kapat</button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                open={confirmState.open}
                message={confirmState.message}
                danger={confirmState.danger}
                onConfirm={() => { if (confirmState.onConfirm) confirmState.onConfirm(); closeConfirm(); }}
                onCancel={closeConfirm}
            />

            <NoticeModal
                open={notice.open}
                message={notice.message}
                danger={notice.danger}
                onClose={() => setNotice({ open: false, message: '', danger: false })}
            />
        </div>
    );
};

export default WithdrawalPoolPage;